import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  makeStyles,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { STATUS_API } from 'src/app/constant/config';
import { MESSAGE } from 'src/app/constant/message';
import {
  createHereSyncTarget,
  deleteHereSyncTarget,
  getHereSyncTargets,
  pushHereAllTargets,
  pushHereSyncTarget,
  updateHereSyncTarget
} from 'src/features/hereSlice';

const EMPTY_FORM = { name: '', baseUrl: '', token: '', enabled: true, kind: 'normalizer' };

const formatTime = value => (value ? new Date(value).toLocaleString('vi-VN') : '—');

/**
 * Hai loại server đích, nghe hai endpoint khác nhau và nhận dữ liệu theo hai đường khác nhau.
 *
 * `normalizer` là loại thường gặp: server bản đồ đã cài, chỉ nhận số nhà ĐÃ DUYỆT qua khối bên
 * dưới. Nút "Cập nhật" ở đây không áp dụng cho nó — bấm vào chỉ nhận 404 vì server bản đồ không
 * có endpoint nhân bản cơ sở dữ liệu.
 */
const KIND = {
  normalizer: {
    label: 'Server bản đồ',
    hint: 'Server OpenStreetMap đã cài. Nhận số nhà đã duyệt qua khối "Duyệt sửa địa chỉ" bên dưới.'
  },
  osm_service: {
    label: 'osm_service khác',
    hint: 'Một bản osm_service ngang hàng. Nhận toàn bộ địa chỉ đã thu thập để nhân bản cơ sở dữ liệu.'
  }
};

const kindOf = target => target.kind || 'normalizer';

/**
 * Khối "Cập nhật server bản đồ".
 *
 * Mỗi dòng là một server OpenStreetMap đã cài đặt; nút "Cập nhật" đẩy phần dữ liệu địa chỉ MỚI
 * sang server đó. Chỉ đẩy phần chưa gửi (dịch vụ tự giữ con trỏ), nên bấm nhiều lần là vô hại.
 */
const HereSyncPanel = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const targets = useSelector(state => state.hereSlice.targets);
  const statusGetTargets = useSelector(state => state.hereSlice.statusGetTargets);
  const statusMutate = useSelector(state => state.hereSlice.statusMutateTarget);
  const statusPush = useSelector(state => state.hereSlice.statusPush);

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const reload = useCallback(() => dispatch(getHereSyncTargets()), [dispatch]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (statusMutate === STATUS_API.SUCCESS || statusPush === STATUS_API.SUCCESS) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusMutate, statusPush]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setOpenForm(true);
  };

  const openEdit = target => {
    // Token không bao giờ được trả về từ API — để trống nghĩa là GIỮ token cũ.
    setForm({ name: target.name, baseUrl: target.base_url, token: '', enabled: target.enabled, kind: kindOf(target) });
    setEditingId(target.id);
    setOpenForm(true);
  };

  const submit = () => {
    const payload = { baseUrl: form.baseUrl.trim(), enabled: form.enabled, kind: form.kind };
    if (form.token) payload.token = form.token;

    if (editingId) dispatch(updateHereSyncTarget({ id: editingId, ...payload }));
    else dispatch(createHereSyncTarget({ ...payload, name: form.name.trim() }));

    setOpenForm(false);
  };

  const canSubmit = form.baseUrl.trim() && (editingId || form.name.trim());

  // Số server nhận đẩy hàng loạt. Server bản đồ không nằm trong nhóm này.
  const mirrorCount = targets.filter(target => kindOf(target) === 'osm_service').length;

  return (
    <Card className={classes.card}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <Box>
            <Typography variant="h5">Server bản đồ</Typography>
            <Typography variant="body2" color="textSecondary">
              Nơi nhận dữ liệu địa chỉ. Server bản đồ nhận số nhà đã duyệt ở khối bên dưới; osm_service ngang hàng nhận
              toàn bộ dữ liệu đã thu thập bằng nút cập nhật.
            </Typography>
          </Box>
          <Box display="flex" gridGap={8} mt={1}>
            <Button variant="outlined" color="primary" onClick={openCreate}>
              Thêm server
            </Button>
            {/* Chỉ hiện khi thật sự có osm_service ngang hàng: lệnh này không đụng tới server bản
                đồ, để nút đứng đó chỉ mời người dùng bấm một thứ không làm gì cho họ. */}
            {mirrorCount > 0 && (
              <Button
                variant="contained"
                color="primary"
                disabled={statusPush === STATUS_API.PENDING}
                onClick={() => dispatch(pushHereAllTargets())}>
                Cập nhật tất cả ({mirrorCount})
              </Button>
            )}
          </Box>
        </Box>

        <Divider className={classes.divider} />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tên</TableCell>
              <TableCell>Địa chỉ server</TableCell>
              <TableCell>Loại</TableCell>
              <TableCell>Bật</TableCell>
              <TableCell align="right">Đã gửi</TableCell>
              <TableCell>Lần cuối</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {targets.map(target => (
              <TableRow key={target.id} hover>
                <TableCell>{target.name}</TableCell>
                <TableCell>{target.base_url}</TableCell>
                <TableCell>
                  {KIND[kindOf(target)]?.label ?? kindOf(target)}
                  {kindOf(target) === 'normalizer' && (
                    <Typography variant="caption" display="block" color="textSecondary">
                      Cập nhật qua khối duyệt bên dưới
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{target.enabled ? 'Có' : 'Không'}</TableCell>
                {/* Con trỏ đẩy hàng loạt chỉ có nghĩa với osm_service ngang hàng; server bản đồ
                    theo dõi tiến độ bằng số đề xuất đã áp dụng ở khối bên dưới. */}
                <TableCell align="right">{kindOf(target) === 'normalizer' ? '—' : target.pushed_total}</TableCell>
                <TableCell>
                  {formatTime(target.last_synced_at)}
                  {target.last_error && (
                    <Typography variant="caption" display="block" className={classes.error}>
                      {target.last_error}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {kindOf(target) === 'osm_service' && (
                    <Button
                      size="small"
                      color="primary"
                      disabled={statusPush === STATUS_API.PENDING || !target.enabled}
                      onClick={() => dispatch(pushHereSyncTarget(target.id))}>
                      Cập nhật
                    </Button>
                  )}
                  <Button size="small" onClick={() => openEdit(target)}>
                    Sửa
                  </Button>
                  <Button size="small" className={classes.error} onClick={() => setConfirmDelete(target)}>
                    Xóa
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!targets.length && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {statusGetTargets === STATUS_API.PENDING ? 'Đang tải...' : 'Chưa khai báo server bản đồ nào'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={openForm} onClose={() => setOpenForm(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Sửa server bản đồ' : 'Thêm server bản đồ'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label="Tên"
            disabled={Boolean(editingId)}
            value={form.name}
            onChange={event => setForm({ ...form, name: event.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label="Địa chỉ server"
            placeholder="https://osm-135.example.vn"
            helperText="Địa chỉ gốc của dịch vụ bản đồ ở server đích, không kèm đường dẫn phía sau."
            value={form.baseUrl}
            onChange={event => setForm({ ...form, baseUrl: event.target.value })}
          />
          {/* Phải khai rõ: hai loại gọi hai endpoint khác nhau, không đoán được từ địa chỉ server. */}
          <TextField
            select
            fullWidth
            margin="dense"
            variant="outlined"
            label="Loại server"
            helperText={KIND[form.kind]?.hint}
            value={form.kind}
            onChange={event => setForm({ ...form, kind: event.target.value })}>
            {Object.keys(KIND).map(key => (
              <MenuItem key={key} value={key}>
                {KIND[key].label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            type="password"
            label="Mã truy cập"
            helperText={editingId ? 'Bỏ trống để giữ mã hiện tại.' : 'Mã do server đích cấp.'}
            value={form.token}
            onChange={event => setForm({ ...form, token: event.target.value })}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.enabled}
                onChange={event => setForm({ ...form, enabled: event.target.checked })}
                color="primary"
              />
            }
            label="Bật đồng bộ"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Hủy</Button>
          <Button color="primary" variant="contained" disabled={!canSubmit} onClick={submit}>
            Lưu
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{MESSAGE.CONFIRM_DELETE_HERE_TARGET}</DialogTitle>
        <DialogContent>
          <Typography>{MESSAGE.CONTENT_DELETE_HERE_TARGET}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Hủy</Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => {
              dispatch(deleteHereSyncTarget(confirmDelete.id));
              setConfirmDelete(null);
            }}>
            Xóa
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

const useStyles = makeStyles(theme => ({
  card: { marginTop: theme.spacing(2) },
  divider: { margin: `${theme.spacing(2)}px 0` },
  error: { color: theme.palette.error.main }
}));

export default HereSyncPanel;
